"use strict";
/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT license. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.isCustomLocation = exports.findMatchingCustomLocations = exports.uniqueId = exports.maintainScrollVisibility = exports.isScrollable = exports.getUpdatedIndex = exports.getActionFromKey = exports.MenuActions = exports.Keys = void 0;
var Keys;
(function (Keys) {
    Keys["Backspace"] = "Backspace";
    Keys["Clear"] = "Clear";
    Keys["Down"] = "ArrowDown";
    Keys["End"] = "End";
    Keys["Enter"] = "Enter";
    Keys["Escape"] = "Escape";
    Keys["Home"] = "Home";
    Keys["Left"] = "ArrowLeft";
    Keys["PageDown"] = "PageDown";
    Keys["PageUp"] = "PageUp";
    Keys["Right"] = "ArrowRight";
    Keys["Space"] = " ";
    Keys["Tab"] = "Tab";
    Keys["Up"] = "ArrowUp";
})(Keys = exports.Keys || (exports.Keys = {}));
var MenuActions;
(function (MenuActions) {
    MenuActions[MenuActions["Close"] = 0] = "Close";
    MenuActions[MenuActions["CloseSelect"] = 1] = "CloseSelect";
    MenuActions[MenuActions["First"] = 2] = "First";
    MenuActions[MenuActions["Last"] = 3] = "Last";
    MenuActions[MenuActions["Next"] = 4] = "Next";
    MenuActions[MenuActions["Open"] = 5] = "Open";
    MenuActions[MenuActions["PageDown"] = 6] = "PageDown";
    MenuActions[MenuActions["PageUp"] = 7] = "PageUp";
    MenuActions[MenuActions["Previous"] = 8] = "Previous";
    MenuActions[MenuActions["Select"] = 9] = "Select";
    MenuActions[MenuActions["Space"] = 10] = "Space";
    MenuActions[MenuActions["Type"] = 11] = "Type";
})(MenuActions = exports.MenuActions || (exports.MenuActions = {}));
// return combobox action from key press
function getActionFromKey(event, menuOpen) {
    const { key, altKey, ctrlKey, metaKey } = event;
    const openKeys = [
        Keys.Down,
        Keys.Up,
        Keys.Enter,
        Keys.Space,
        Keys.Home,
        Keys.End,
    ]; // all keys that will open the combo
    // handle opening when closed
    if (!menuOpen && openKeys.some((k) => k === key)) {
        return MenuActions.Open;
    }
    // handle typing characters when open or closed
    if (key === Keys.Backspace ||
        key === Keys.Clear ||
        (key.length === 1 && key !== " " && !altKey && !ctrlKey && !metaKey)) {
        return MenuActions.Type;
    }
    // handle keys when open
    if (menuOpen) {
        if ((key === Keys.Down && !altKey) || key === Keys.Right) {
            return MenuActions.Next;
        }
        else if (key === Keys.Up && altKey) {
            return MenuActions.CloseSelect;
        }
        else if (key === Keys.Up || key === Keys.Left) {
            return MenuActions.Previous;
        }
        else if (key === Keys.Home) {
            return MenuActions.First;
        }
        else if (key === Keys.End) {
            return MenuActions.Last;
        }
        else if (key === Keys.PageUp) {
            return MenuActions.PageUp;
        }
        else if (key === Keys.PageDown) {
            return MenuActions.PageDown;
        }
        else if (key === Keys.Escape) {
            return MenuActions.Close;
        }
        else if (key === Keys.Enter) {
            return MenuActions.CloseSelect;
        }
        else if (key === Keys.Space) {
            return MenuActions.Space;
        }
    }
}
exports.getActionFromKey = getActionFromKey;
// get updated option index
function getUpdatedIndex(current, max, action) {
    switch (action) {
        case MenuActions.First:
            return 0;
        case MenuActions.Last:
            return max;
        case MenuActions.Previous:
            return Math.max(0, current - 1);
        case MenuActions.Next:
            return Math.min(max, current + 1);
        default:
            return current;
    }
}
exports.getUpdatedIndex = getUpdatedIndex;
// check if an element is currently scrollable
function isScrollable(element) {
    return element.current
        ? element.current.clientHeight < element.current.scrollHeight
        : false;
}
exports.isScrollable = isScrollable;
// ensure given child element is within the parent's visible scroll area
function maintainScrollVisibility(activeElement, scrollParent) {
    if (!activeElement.current || !scrollParent.current) {
        return;
    }
    const { offsetHeight, offsetTop } = activeElement.current;
    const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent.current;
    const isAbove = offsetTop < scrollTop;
    const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;
    if (isAbove) {
        const maybeStrong = activeElement.current.previousElementSibling;
        if ((maybeStrong === null || maybeStrong === void 0 ? void 0 : maybeStrong.tagName) === "STRONG") {
            scrollParent.current.scrollTo(0, maybeStrong.offsetTop);
        }
        else {
            scrollParent.current.scrollTo(0, offsetTop);
        }
    }
    else if (isBelow) {
        scrollParent.current.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
    }
}
exports.maintainScrollVisibility = maintainScrollVisibility;
// generate unique ID, the quick 'n dirty way
let idIndex = 0;
function uniqueId() {
    return `sui-${++idIndex}`;
}
exports.uniqueId = uniqueId;
function findMatchingCustomLocations(input, locations) {
    const matches = [];
    if (input === undefined || input.length < 1) {
        return matches;
    }
    const inputLowerCase = input.toLowerCase();
    const length = inputLowerCase.length;
    const matchedURLs = new Set([]);
    outer_for_loop: for (const only_starts_with of [true, false]) {
        if (locations === null || locations === void 0 ? void 0 : locations.length) {
            for (const h of locations) {
                if (matches.length >= 5) {
                    break outer_for_loop;
                }
                if (matchedURLs.has(h.url_en)) {
                    continue;
                }
                const nameLowerCase = h.name.toLowerCase();
                if (only_starts_with && !nameLowerCase.startsWith(inputLowerCase)) {
                    continue;
                }
                let i = 0;
                const matched_substrings = [];
                while (true) {
                    const offset = nameLowerCase.indexOf(inputLowerCase, i);
                    if (offset < 0) {
                        break;
                    }
                    matched_substrings.push({ offset, length });
                    i = offset + length;
                }
                if (matched_substrings.length > 0) {
                    matches.push(Object.assign(Object.assign({}, h), { matched_substrings }));
                    matchedURLs.add(h.url_en);
                }
            }
        }
    }
    return matches;
}
exports.findMatchingCustomLocations = findMatchingCustomLocations;
function isCustomLocation(home) {
    return home.url_en !== undefined;
}
exports.isCustomLocation = isCustomLocation;
